import type {
  ApiErrorResponse,
  AuthStatusResponse,
  DiagnosticsResponse,
  ExternalContactView,
  HealthResponse,
  JssdkSignatureResponse,
  MemberProfile,
  RuntimeConfigResponse
} from "../shared/types";

export class ApiClientError extends Error {
  constructor(
    readonly status: number,
    readonly payload: ApiErrorResponse
  ) {
    super(payload.error.message);
    this.name = "ApiClientError";
  }
}

async function requestJson<T>(input: RequestInfo | URL, init?: RequestInit): Promise<T> {
  const response = await fetch(input, {
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {})
    },
    ...init
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const payload = (await response.json()) as T | ApiErrorResponse;
  if (!response.ok) {
    throw new ApiClientError(response.status, payload as ApiErrorResponse);
  }

  return payload as T;
}

export const api = {
  health: () => requestJson<HealthResponse>("/api/health"),
  authStatus: () => requestJson<AuthStatusResponse>("/api/auth/status"),
  runtimeConfig: () => requestJson<RuntimeConfigResponse>("/api/runtime-config"),
  me: () => requestJson<MemberProfile>("/api/me"),
  diagnostics: () => requestJson<DiagnosticsResponse>("/api/diagnostics"),
  logout: () =>
    requestJson<void>("/api/auth/logout", {
      method: "POST"
    }),
  signature: (url: string) =>
    requestJson<JssdkSignatureResponse>("/api/wecom/jssdk/signature", {
      method: "POST",
      body: JSON.stringify({ url })
    }),
  externalContact: (externalUserId: string) =>
    requestJson<ExternalContactView>(`/api/external-contact/${encodeURIComponent(externalUserId)}`, {
      cache: "no-store"
    })
};
